import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useRadioGroup } from '@material-ui/core/RadioGroup';
import useControlled from '@material-ui/core/utils/useControlled';

import { Check } from '@material-ui/icons';
import clsx from 'clsx';

import RadioIcon from '../RadioIcon';

export const styles = (theme) => ({
  /* Styles applied to the root element. */
  root: {
    padding: '7px 12px 7px 16px',
    margin: 0,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    transition: 'background-color 0.3s ease',
    minHeight: '34px',

    '&$checked': {
      backgroundColor: theme.palette.primary.light,
      borderColor: 'transparent',

      '& $label': {
        fontWeight: 'bold',
        letterSpacing: 'normal',
        backgroundColor: 'transparent',
      },
    },

    '& $label': {
      padding: 0,
      marginLeft: 0,
      letterSpacing: '0.2px',
    },

    '&$disabled': {
      color: '#b4b4b4',
      backgroundColor: theme.palette.action.disabledBackground,
      borderColor: '#b4b4b4',

      '& $control': {
        color: 'inherit',
      },
      '& $label': {
        color: 'inherit',
      },
    },
  },
  fullWidth: {
    width: '100%',
  },
  /* Pseudo-class applied to the root element if `disabled={true}`. */
  disabled: {},
  /* Pseudo-class applied to the root element if `checked={true}`. */
  checked: {},
  /* Styles applied to the label's Typography component. */
  label: {
    marginRight: 'auto',
    userSelect: 'none',
    lineHeight: '1.2',
  },

  labelSecondary: {
    display: 'block',
    fontWeight: 'inherit',
  },

  inactive: {
    pointerEvents: 'none',
  },

  control: {
    padding: '0',
    alignSelf: 'center',

    '& $checkIcon': {
      visibility: 'hidden',
    },

    '&$checked': {
      '& $checkIcon': {
        visibility: 'visible',
      },
    },
  },

  controlRadio: {
    // alignSelf: 'flex-start',
    marginLeft: '-6px',
    marginRight: '10px',
  },

  icon: {
    fontSize: '22px',
  },

  hideControl: {
    '& $control': {
      display: 'none',
    },
  },

  checkIcon: {},
});

const SwitchChip = memo(function SwitchChip(props) {
  const {
    inactive,
    fullWidth,
    className,
    classes,
    defaultChecked,
    checked: checkedProp,
    onChange: onChangeProp,
    name: nameProp,
    labelPlacement: labelPlacementProp,
    checkedIcon: CheckedIcon,
    radio,
    label,
    labelPrimary,
    labelSecondary,
    hideControl,
    ...rest
  } = props;

  /**
   * Checkbox control
   */
  const [checkedState, setCheckedState] = useControlled({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
    name: 'SwitchChip',
    state: 'checked',
  });

  /**
   * Radio control
   */
  const radioGroup = useRadioGroup();

  let checked = radio ? checkedProp : checkedState;
  let name = nameProp;

  if (radioGroup) {
    if (typeof checked === 'undefined') {
      checked = radioGroup.value === props.value;
    }
    if (typeof name === 'undefined') {
      name = radioGroup.name;
    }
  }

  const onChange = useCallback(
    (event, eventChecked) => {
      onChangeProp?.(event, eventChecked);

      if (radio) {
        radioGroup?.onChange?.(event, eventChecked);
      } else {
        setCheckedState(eventChecked);
      }
    },
    [onChangeProp, radio, setCheckedState, radioGroup],
  );

  const formControlLabelClasses = useMemo(
    () => ({
      label: classes.label,
    }),
    [classes.label],
  );

  return (
    <FormControlLabel
      className={clsx(className, classes.root, {
        [classes.checked]: checked,
        [classes.fullWidth]: fullWidth,
        [classes.inactive]: inactive,
        [classes.hideControl]: hideControl,
      })}
      classes={formControlLabelClasses}
      labelPlacement={radio && !labelPlacementProp ? 'end' : 'start'}
      control={
        !radio ? (
          <Checkbox
            name={name}
            icon={<Check className={clsx(classes.icon, classes.checkIcon)} />}
            checkedIcon={
              <Check className={clsx(classes.icon, classes.checkIcon)} />
            }
            className={classes.control}
            checked={checked}
            onChange={onChange}
          />
        ) : (
          <Radio
            name={name}
            icon={<RadioIcon className={classes.icon} />}
            checkedIcon={
              CheckedIcon === 'check' ? (
                <Check className={clsx(classes.icon, classes.checkIcon)} />
              ) : (
                <RadioIcon checked className={classes.icon} />
              )
            }
            className={clsx(classes.control, classes.controlRadio)}
            checked={checked}
            onChange={onChange}
          />
        )
      }
      label={
        label || (
          <>
            {labelPrimary}
            {labelSecondary && (
              <Typography
                component="span"
                className={classes.labelSecondary}
                variant="caption"
              >
                {labelSecondary}
              </Typography>
            )}
          </>
        )
      }
      {...rest}
    />
  );
});

SwitchChip.propTypes = {
  inactive: PropTypes.bool,
  className: PropTypes.string,
  classes: PropTypes.object,
  defaultChecked: PropTypes.bool,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  labelPlacement: PropTypes.string,
  fullWidth: PropTypes.bool,
  name: PropTypes.string,
  radio: PropTypes.bool,
  label: PropTypes.node,
  checkedIcon: PropTypes.string,
  labelPrimary: PropTypes.node,
  labelSecondary: PropTypes.node,
  hideControl: PropTypes.bool,
};

export default withStyles(styles, { name: 'MuiSwitchChip' })(SwitchChip);
